import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { getConcernDomains, StoredConcernData, StoredConcernGroup, StoredConcernItem, CONCERN_STORAGE_KEY } from '../../types/concerns';
import { appConfig } from '../../config/configManager';
import { useProfileStore } from '../../contexts/StoreContext';
import { ConcernState, calculateAgeInMonths } from '../../types/profile';
import { checkDomainStaleness, loadConcernFreshnessThreshold, loadShowLastUpdateSetting, formatComputationTimestamp } from '../../utils/milestoneStaleness';

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Unique concern mvarid composed from the question's MvarName + the answer keyValue */
const concernMvarid = (group: StoredConcernGroup, item: StoredConcernItem): string =>
  `${group.mvarName.toLowerCase()}_${item.keyValue}`;

const getGlobalStorage = () =>
  (globalThis as any).__childNavStorage ?? null;

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const ConcernsView: React.FC<{ initialDomainKey?: string; showBackToSnapshot?: boolean; onBackToSnapshot?: () => void; onUpdateActions?: () => void }> = ({ initialDomainKey, showBackToSnapshot, onBackToSnapshot, onUpdateActions }) => {
  // Config & profile
  const config = appConfig.getConfig();

  const profileStore = useProfileStore();
  const { getActiveProfile, updateProfile, markProfileDirty } = profileStore();
  const activeProfile = getActiveProfile();

  // Domain tabs
  const domains = useMemo(() => getConcernDomains(), []);
  const [selectedDomainIndex, setSelectedDomainIndex] = useState(() => {
    if (!initialDomainKey) return 0;
    const idx = getConcernDomains().findIndex(d => d.key === initialDomainKey);
    return idx >= 0 ? idx : 0;
  });
  const selectedDomain = domains[selectedDomainIndex];

  // Sync domain if navigated to with a new initialDomainKey
  useEffect(() => {
    if (!initialDomainKey) return;
    const idx = domains.findIndex(d => d.key === initialDomainKey);
    if (idx >= 0) setSelectedDomainIndex(idx);
  }, [initialDomainKey, domains]);

  // Stored concern data loaded from storage
  const [concernData, setConcernData] = useState<StoredConcernData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLastUpdate, setShowLastUpdate] = useState(true);
  const [concernFreshnessThreshold, setConcernFreshnessThreshold] = useState('2weeks');
  const [showDebugMvarName, setShowDebugMvarName] = useState(false);
  // Latest snapshot adminresponse variables (keyed by lowercase name), null = not yet loaded
  const [snapshotVars, setSnapshotVars] = useState<Record<string, string> | null>(null);

  // Load concern data and freshness settings from storage
  const loadConcernData = useCallback(async () => {
    setLoading(true);
    try {
      const storage = getGlobalStorage();
      if (!storage) {
        console.warn('ConcernsView: global storage not available');
        setLoading(false);
        return;
      }
      const raw = await storage.getItem(CONCERN_STORAGE_KEY);
      if (raw) {
        const parsed: StoredConcernData = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setConcernData(parsed);
        console.log(`ConcernsView: loaded ${parsed.groups.length} concern groups from storage`);
      } else {
        console.log('ConcernsView: no concern data in storage (run Extract Concerns in DebugView)');
        setConcernData(null);
      }
      const freshness = await loadConcernFreshnessThreshold(storage);
      setConcernFreshnessThreshold(freshness);
      const showUpdate = await loadShowLastUpdateSetting(storage);
      setShowLastUpdate(showUpdate);
      const debugState = await storage.getItem('debug_show_info');
      setShowDebugMvarName(debugState === 'true');
      // Load latest snapshot adminresponse for BPSC/POSI/PPSC variables
      try {
        const profileId = activeProfile?.id;
        if (profileId) {
          const { AdminResponseStorage } = await import('../../storage/AdminResponseStorage');
          const ars = new AdminResponseStorage(storage);
          const profileResponses = await ars.getProfileResponses(profileId);
          const snapshots = (profileResponses.responses ?? []).filter((r: any) =>
            r.recSurvey === '4' || r.recAssessment === '4'
          );
          // Use the most recent snapshot
          snapshots.sort((a: any, b: any) =>
            new Date(b.recUTM || b.recDate || 0).getTime() -
            new Date(a.recUTM || a.recDate || 0).getTime()
          );
          const latest = snapshots[0];
          if (latest?.responseData) {
            const vars: Record<string, string> = {};
            for (const d of latest.responseData) {
              if (d.name) vars[d.name.toLowerCase()] = d.value ?? '';
            }
            setSnapshotVars(vars);
          } else {
            setSnapshotVars({});
          }
        } else {
          setSnapshotVars({});
        }
      } catch {
        setSnapshotVars({});
      }
    } catch (error) {
      console.error('ConcernsView: failed to load concern data', error);
    } finally {
      setLoading(false);
    }
  }, [activeProfile?.id]);

  // Initial load on mount
  useEffect(() => {
    loadConcernData();
  }, [loadConcernData]);

  // Listen for concern storage invalidation signals (e.g. from useMilestoneSync auto-extract)
  useEffect(() => {
    const lastSeenRef = { value: (globalThis as any).__concernStorageInvalidated ?? 0 };
    const checkForInvalidation = () => {
      const current = (globalThis as any).__concernStorageInvalidated ?? 0;
      if (current > lastSeenRef.value) {
        lastSeenRef.value = current;
        console.log('🔄 ConcernsView: concern storage invalidated, reloading…');
        loadConcernData();
      }
    };
    checkForInvalidation();
    const interval = setInterval(checkForInvalidation, 1000);
    return () => clearInterval(interval);
  }, [loadConcernData]);

  // Groups for the currently selected domain tab
  // While concern definitions are still being fetched from storage we show a brief message
  // rather than silently rendering an empty/unresponsive view.
  // (loading is set to true on every loadConcernData call and cleared in the finally block.)

  const currentGroups = useMemo<StoredConcernGroup[]>(() => {
    if (!concernData) return [];
    // Age gate: hide cn_adhd (Behavior Control Concerns) for children under 2 years (24 months)
    const childAgeMonths = activeProfile?.dob ? calculateAgeInMonths(activeProfile.dob) : 0;
    const groups = concernData.groups.filter(g => {
      if (g.domainKey !== selectedDomain?.key) return false;
      if (g.mvarName.toLowerCase() === 'cn_adhd' && childAgeMonths < 24) return false;
      return true;
    });
    // For the family domain show cn_familyconcerns before cn_experience
    if (selectedDomain?.key === 'family') {
      const ORDER = ['cn_familyconcerns', 'cn_experience'];
      return [...groups].sort((a, b) => {
        const ai = ORDER.indexOf(a.mvarName.toLowerCase());
        const bi = ORDER.indexOf(b.mvarName.toLowerCase());
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    }
    return groups;
  }, [concernData, selectedDomain]);

  // Concern state helpers
  const isConcernChecked = useCallback(
    (mvarid: string): boolean => {
      if (!activeProfile?.concernsstate) return false;
      const lower = mvarid.toLowerCase();
      const entry = activeProfile.concernsstate.find((c: ConcernState) => c.mvarid.toLowerCase() === lower);
      return entry?.state === 'yes';
    },
    [activeProfile]
  );

  const toggleConcern = useCallback(
    (mvarid: string) => {
      if (!activeProfile) return;
      const existing: ConcernState[] = activeProfile.concernsstate ?? [];
      const lower = mvarid.toLowerCase();
      const idx = existing.findIndex((c: ConcernState) => c.mvarid.toLowerCase() === lower);
      const nowChecked = idx >= 0 ? existing[idx].state !== 'yes' : true;
      // CN_Family is inverted: action is shown/used when unchecked, so checking it should clear the action
      const invertedGroup = mvarid.toLowerCase().startsWith('cn_family_');
      const shouldClearAction = invertedGroup ? nowChecked : !nowChecked;
      let updated: ConcernState[];
      if (idx >= 0) {
        updated = [
          ...existing.slice(0, idx),
          { ...existing[idx], state: nowChecked ? 'yes' : 'no', action: shouldClearAction ? undefined : existing[idx].action, lastUpdated: new Date().toISOString() },
          ...existing.slice(idx + 1),
        ];
      } else {
        updated = [
          ...existing,
          { mvarid: lower, state: 'yes', lastUpdated: new Date().toISOString(), notes: '' },
        ];
      }
      updateProfile(activeProfile.id, { concernsstate: updated });
      markProfileDirty(activeProfile.id, `Concern toggled: ${lower}`);
    },
    [activeProfile, updateProfile, markProfileDirty]
  );

  const getConcernAction = useCallback(
    (mvarid: string): number | undefined => {
      if (!activeProfile?.concernsstate) return undefined;
      const lower = mvarid.toLowerCase();
      const entry = activeProfile.concernsstate.find((c: ConcernState) => c.mvarid.toLowerCase() === lower);
      return entry?.action;
    },
    [activeProfile]
  );

  const setConcernAction = useCallback(
    (mvarid: string, value: number) => {
      if (!activeProfile) return;
      const existing: ConcernState[] = activeProfile.concernsstate ?? [];
      const lower = mvarid.toLowerCase();
      const idx = existing.findIndex((c: ConcernState) => c.mvarid.toLowerCase() === lower);
      // Toggle off if same value selected again
      const newAction = (idx >= 0 && existing[idx].action === value) ? undefined : value;
      let updated: ConcernState[];
      if (idx >= 0) {
        updated = [
          ...existing.slice(0, idx),
          { ...existing[idx], action: newAction, lastUpdated: new Date().toISOString() },
          ...existing.slice(idx + 1),
        ];
      } else {
        updated = [
          ...existing,
          { mvarid: lower, state: 'no', action: newAction, lastUpdated: new Date().toISOString(), notes: '' },
        ];
      }
      updateProfile(activeProfile.id, { concernsstate: updated });
      markProfileDirty(activeProfile.id, `Concern action updated: ${lower}=${newAction ?? 'cleared'}`);
    },
    [activeProfile, updateProfile, markProfileDirty]
  );

  const ACTION_BUTTONS: { value: number; label: string; color: string }[] = [
    { value: 1, label: 'Track',     color: '#3498db' },
    { value: 2, label: 'Manage',    color: '#10b981' },
    { value: 3, label: 'Seek Help', color: '#e74c3c' },
  ];

  const handlePrevDomain = () => {
    if (selectedDomainIndex > 0) navigateToDomain(selectedDomainIndex - 1);
  };
  const handleNextDomain = () => {
    if (selectedDomainIndex < domains.length - 1) navigateToDomain(selectedDomainIndex + 1);
  };

  // ─── Mark domain as reviewed on explicit user navigation ────────────────────

  /** Upsert a sentinel ConcernState entry that records when this domain was last reviewed */
  const markDomainReviewed = useCallback(
    (domainKey: string) => {
      if (!activeProfile) return;
      const sentinelId = `domain_viewed_${domainKey}`;
      const now = new Date().toISOString();
      const existing: ConcernState[] = activeProfile.concernsstate ?? [];
      const idx = existing.findIndex((c: ConcernState) => c.mvarid === sentinelId);
      let updated: ConcernState[];
      if (idx >= 0) {
        updated = [
          ...existing.slice(0, idx),
          { ...existing[idx], lastUpdated: now },
          ...existing.slice(idx + 1),
        ];
      } else {
        updated = [...existing, { mvarid: sentinelId, state: 'no', lastUpdated: now, notes: '' }];
      }
      updateProfile(activeProfile.id, { concernsstate: updated });
    },
    [activeProfile, updateProfile]
  );

  const handleUpdateActions = useCallback(() => {
    if (selectedDomain) markDomainReviewed(selectedDomain.key);
    onUpdateActions?.();
  }, [selectedDomain, markDomainReviewed, onUpdateActions]);

  /**
   * Navigate to a domain by index, stamping the domain being LEFT as reviewed.
   * Only call this for explicit user-driven navigation (tab tap, arrow buttons).
   * Do NOT use for programmatic syncs (initialDomainKey) to avoid false stamps.
   */
  const navigateToDomain = useCallback(
    (index: number) => {
      // Stamp the domain we're leaving, not the one we're going to
      const leavingKey = domains[selectedDomainIndex]?.key;
      if (leavingKey) markDomainReviewed(leavingKey);
      setSelectedDomainIndex(index);
    },
    [domains, selectedDomainIndex, markDomainReviewed]
  );

  // ─── Domain staleness ────────────────────────────────────────────────────────

  /** ISO timestamp of the most recent ConcernState update in the selected domain, or null */
  const domainLastUpdated = useMemo<string | null>(() => {
    const states: ConcernState[] = activeProfile?.concernsstate ?? [];
    // Always include the domain-viewed sentinel for this domain
    const sentinelId = `domain_viewed_${selectedDomain?.key ?? ''}`;
    let domainMvarids: Set<string>;
    if (selectedDomain?.key === 'measures') {
      domainMvarids = new Set(
        // Match cn_{code}_measure format, legacy {dk}_concern format, and devage_{dk} for migration
        states.filter(c => {
          const id = c.mvarid as string;
          return (id.startsWith('cn_') && id.endsWith('_measure')) || id.endsWith('_concern') || id.startsWith('devage_');
        }).map(c => c.mvarid)
      );
    } else {
      const groupMvarids = (concernData?.groups ?? [])
        .filter(g => g.domainKey === selectedDomain?.key)
        .flatMap(g => g.items.map(item => `${g.mvarName}_${item.keyValue}`));
      domainMvarids = new Set(groupMvarids);
    }
    domainMvarids.add(sentinelId);
    const timestamps = states
      .filter(c => domainMvarids.has(c.mvarid) && c.lastUpdated)
      .map(c => c.lastUpdated);
    return timestamps.length > 0 ? timestamps.reduce((a, b) => (a > b ? a : b)) : null;
  }, [activeProfile, concernData, selectedDomain]);

  const domainStaleness = useMemo(
    () => checkDomainStaleness(domainLastUpdated, concernFreshnessThreshold),
    [domainLastUpdated, concernFreshnessThreshold]
  );


  // â”€â”€â”€ Render helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const renderConcernItem = (group: StoredConcernGroup, item: StoredConcernItem) => {
    const mvarid = concernMvarid(group, item);
    const checked = isConcernChecked(mvarid);
    const action = getConcernAction(mvarid);
    const invertedGroup = group.mvarName.toLowerCase() === 'cn_family';
    const showActions = invertedGroup ? !checked : checked;
    return (
      <View
        key={`${group.mvarName}_${item.keyValue}`}
        style={[styles.itemCard, checked ? styles.itemCardChecked : null]}
      >
        <TouchableOpacity
          style={styles.itemCheckRow}
          onPress={() => toggleConcern(mvarid)}
          activeOpacity={0.75}
        >
          <View style={[styles.checkbox, checked ? styles.checkboxChecked : null]}>
            {checked ? <Text style={styles.checkmark}>{'✔'}</Text> : null}
          </View>
          <Text style={[styles.concernText, checked ? styles.concernTextChecked : null]}>{showDebugMvarName ? `${item.text} (${item.mvarName ?? concernMvarid(group, item)})` : item.text}</Text>
        </TouchableOpacity>
        {showActions ? (
          <View style={styles.actionButtons}>
            {ACTION_BUTTONS.map(btn => {
              const selected = action === btn.value;
              return (
                <TouchableOpacity
                  key={btn.value}
                  style={[styles.actionButton, selected ? { backgroundColor: btn.color, borderColor: btn.color } : null]}
                  onPress={() => setConcernAction(mvarid, btn.value)}
                  activeOpacity={0.8}
                >
                  <Text style={[styles.actionButtonText, selected ? styles.actionButtonTextSelected : null]}>
                    {btn.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ) : null}
      </View>
    );
  };

  const CONCERN_GROUP_TITLES: Record<string, string> = {
      cn_behaviorext:    'Child Behavior Concerns',
    cn_medical:        'Current Medical Concerns',
    cn_medicalhistory: 'Medical History Concerns',
    cn_experience:     'Has your child ever experienced ...',
    cn_familyconcerns: 'Family Protective Factors Concerns',
    cn_adhd:           'Behavior Control Concerns',
  };

  const getGroupTitle = (mvarName: string): string =>
    CONCERN_GROUP_TITLES[mvarName.toLowerCase()] ?? mvarName;

  const renderGroup = (group: StoredConcernGroup) => (
    <View key={group.mvarName} style={styles.groupCard}>
      <Text style={styles.groupCardHeader}>{getGroupTitle(group.mvarName)}</Text>
      <View style={styles.groupItemsContainer}>
        {group.items.map(item => renderConcernItem(group, item))}
      </View>
    </View>
  );

  // ─── Measures (devage) domain ──────────────────────────────────────────────

  const DEVAGE_DOMAIN_NAMES: Record<string, string> = {
    communication:   'Communication',
    motor:           'Motor Skills',
    finemotor:       'Fine Motor',
    grossmotor:      'Gross Motor',
    cognitive:       'Cognitive',
    socialemotional: 'Social-Emotional',
  };

  const getResultLabel = (result: number | null): string => {
    if (result === null || result === undefined || typeof result !== 'number' || isNaN(result)) return '';
    if (result <= 0)  return 'Below Cutoff';
    if (result < 50)  return 'Close to Cutoff';
    if (result < 100) return 'Above Cutoff';
    return 'Above the Mean';
  };

  const DEVAGE_DOMAIN_ICONS: Record<string, string> = {
    communication:   '💬',
    motor:           '🏃',
    finemotor:       '✋',
    grossmotor:      '🤸',
    cognitive:       '🧠',
    socialemotional: '😊',
  };

  const getResultBgColor = (result: number | null): string => {
    if (result === null || result === undefined || typeof result !== 'number' || isNaN(result)) return 'transparent';
    if (result <= 0) return '#ff7a7aff';
    if (result < 50)  return '#edff48ff';
    if (result < 100) return '#b7e0c1ff';
    return '#5cb85c';
  };

  const renderMeasuresDomain = () => {
    const isPremium = appConfig.isPremium();
    const devresult = activeProfile?.devresultscore as Record<string, number | null> | undefined;
    const domainKeys: string[] = isPremium
      ? ['communication', 'grossmotor', 'finemotor', 'cognitive', 'socialemotional']
      : ['communication', 'motor', 'cognitive', 'socialemotional'];

    const rows = domainKeys
      .filter(dk => devresult && devresult[dk] != null)
      .map(dk => {
        // mvarid matches AppProfileDto MVarDto.Name: backend cn_{code}_measure naming convention
        const CONCERN_MVARID: Record<string, string> = {
          communication:   'cn_lc_measure',
          finemotor:       'cn_fm_measure',
          grossmotor:      'cn_gm_measure',
          cognitive:       'cn_cg_measure',
          socialemotional: 'cn_se_measure',
          motor:           'cn_mt_measure',
        };
        const mvarid = CONCERN_MVARID[dk] ?? `${dk}_concern`;
        const result = devresult![dk] as number;
        const checked = isConcernChecked(mvarid);
        const action = getConcernAction(mvarid);
        const bgColor = getResultBgColor(result);
        const resultStr = Math.round(result).toString();
        const resultLabel = getResultLabel(result);
        const domainLabel = `${DEVAGE_DOMAIN_NAMES[dk] ?? dk}${resultLabel ? ` – ${resultLabel}` : ''}`;
        return (
          <View key={mvarid} style={[styles.itemCard, checked ? styles.itemCardChecked : null]}>
            <TouchableOpacity style={styles.itemCheckRow} onPress={() => toggleConcern(mvarid)} activeOpacity={0.75}>
              <View style={[styles.checkbox, checked ? styles.checkboxChecked : null]}>
                {checked ? <Text style={styles.checkmark}>{'✔'}</Text> : null}
              </View>
              <Text style={[styles.concernText, checked ? styles.concernTextChecked : null, { flex: 1 }]}>
                {DEVAGE_DOMAIN_ICONS[dk] ?? '�'}{'  '}{domainLabel}{showDebugMvarName ? ` (${mvarid})` : ''}
              </Text>
              <Text style={{
                fontSize: 13,
                fontWeight: 'bold',
                color: bgColor === 'transparent' ? '#7f8c8d' : '#333',
                backgroundColor: bgColor,
                borderRadius: 6,
                paddingVertical: 4,
                paddingHorizontal: 14,
                overflow: 'hidden',
                marginLeft: 8,
              }}>{resultStr}</Text>
            </TouchableOpacity>
            {checked ? (
              <View style={styles.actionButtons}>
                {ACTION_BUTTONS.map(btn => {
                  const selected = action === btn.value;
                  return (
                    <TouchableOpacity
                      key={btn.value}
                      style={[styles.actionButton, selected ? { backgroundColor: btn.color, borderColor: btn.color } : null]}
                      onPress={() => setConcernAction(mvarid, btn.value)}
                      activeOpacity={0.8}
                    >
                      <Text style={[styles.actionButtonText, selected ? styles.actionButtonTextSelected : null]}>
                        {btn.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        );
      });

    // ── Behavior Concerns card: BPSC / POSI / PPSC from latest snapshot adminresponse ──
    const BPSC_RESULT: Record<string, string> = {
      '1': 'Above Average', '2': 'Average', '3': 'Near Cutoff', '4': 'Below Cutoff',
    };
    const POSI_RESULT: Record<string, string> = { '0': 'Negative', '1': 'Positive' };
    const PPSC_RESULT: Record<string, string> = { '0': 'Negative', '1': 'At Risk' };

    const bpscBg = (r: string | undefined) => {
      if (!r) return 'transparent';
      if (r === '4') return '#ff7a7aff';
      if (r === '3') return '#edff48ff';
      return '#b7e0c1ff';
    };
    const posiBg = (r: string | undefined) => (!r ? 'transparent' : r === '1' ? '#ff7a7aff' : '#b7e0c1ff');
    const ppscBg = (r: string | undefined) => (!r ? 'transparent' : r === '1' ? '#edff48ff' : '#b7e0c1ff');

    interface ScreeningRow {
      mvarid: string; icon: string; label: string;
      scoreKey: string; resultKey: string;
      resultMap: Record<string, string>;
      bgFn: (r: string | undefined) => string;
    }
    const SCREENING_ROWS: ScreeningRow[] = [
      { mvarid: 'cn_inflex_measure', icon: '🤯', label: 'Inflexibility (BPSC)',   scoreKey: 'bpsc_inflex_percentile',  resultKey: 'bpsc_inflex_result',  resultMap: BPSC_RESULT, bgFn: bpscBg },
      { mvarid: 'cn_irrit_measure',  icon: '😠', label: 'Irritability (BPSC)',    scoreKey: 'bpsc_irr_percentile',    resultKey: 'bpsc_irr_result',     resultMap: BPSC_RESULT, bgFn: bpscBg },
      { mvarid: 'cn_rout_measure',   icon: '🔄', label: 'Routine (BPSC)',         scoreKey: 'bpsc_routine_percentile', resultKey: 'bpsc_routine_result',  resultMap: BPSC_RESULT, bgFn: bpscBg },
      { mvarid: 'cn_posi_measure',   icon: '👨‍👩‍👧', label: 'Social Interaction (POSI)', scoreKey: 'posi_sum',          resultKey: 'posi_result',         resultMap: POSI_RESULT, bgFn: posiBg },
      { mvarid: 'cn_ppsc_measure',   icon: '🧠', label: 'Preschool Symptoms (PPSC)', scoreKey: 'ppsc_sum',            resultKey: 'ppsc_result',         resultMap: PPSC_RESULT, bgFn: ppscBg },
    ];

    const behaviorRows = snapshotVars !== null
      ? SCREENING_ROWS
          .filter(row => {
            // Gate on resultKey (always populated when assessment done); fall back to scoreKey
            const r = snapshotVars[row.resultKey];
            const v = snapshotVars[row.scoreKey];
            return (r !== undefined && r !== '') || (v !== undefined && v !== '');
          })
          .map(row => {
            const scoreStr    = snapshotVars![row.scoreKey] ?? '';
            const resultCode  = snapshotVars![row.resultKey];
            const resultLabel = row.resultMap[resultCode ?? ''] ?? resultCode ?? '';
            const bgColor     = row.bgFn(resultCode);
            const mvarid      = row.mvarid;
            const checked     = isConcernChecked(mvarid);
            const action      = getConcernAction(mvarid);
            const displayLabel = `${row.label}${resultLabel ? ` – ${resultLabel}` : ''}`;
            return (
              <View key={mvarid} style={[styles.itemCard, checked ? styles.itemCardChecked : null]}>
                <TouchableOpacity style={styles.itemCheckRow} onPress={() => toggleConcern(mvarid)} activeOpacity={0.75}>
                  <View style={[styles.checkbox, checked ? styles.checkboxChecked : null]}>
                    {checked ? <Text style={styles.checkmark}>{'✔'}</Text> : null}
                  </View>
                  <Text style={[styles.concernText, checked ? styles.concernTextChecked : null, { flex: 1 }]}>
                    {row.icon}{'  '}{displayLabel}{showDebugMvarName ? ` (${mvarid})` : ''}
                  </Text>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: 'bold',
                    color: bgColor === 'transparent' ? '#7f8c8d' : '#333',
                    backgroundColor: bgColor,
                    borderRadius: 6,
                    paddingVertical: 4,
                    paddingHorizontal: 14,
                    overflow: 'hidden',
                    marginLeft: 8,
                  }}>{scoreStr}</Text>
                </TouchableOpacity>
                {checked ? (
                  <View style={styles.actionButtons}>
                    {ACTION_BUTTONS.map(btn => {
                      const selected = action === btn.value;
                      return (
                        <TouchableOpacity
                          key={btn.value}
                          style={[styles.actionButton, selected ? { backgroundColor: btn.color, borderColor: btn.color } : null]}
                          onPress={() => setConcernAction(mvarid, btn.value)}
                          activeOpacity={0.8}
                        >
                          <Text style={[styles.actionButtonText, selected ? styles.actionButtonTextSelected : null]}>
                            {btn.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ) : null}
              </View>
            );
          })
      : [];

    if (rows.length === 0 && behaviorRows.length === 0) {
      return (
        <View style={styles.centeredContainer}>
          <Text style={styles.centeredText}>
            {'No developmental age data available.\n\nUpdate milestones in Milestones view to see measurements here.'}
          </Text>
        </View>
      );
    }

    return (
      <View>
        {rows.length > 0 && (
          <View style={styles.groupCard}>
            <Text style={styles.groupCardHeader}>Developmental Concerns</Text>
            <View style={styles.groupItemsContainer}>{rows}</View>
          </View>
        )}
        {behaviorRows.length > 0 && (
          <View style={styles.groupCard}>
            <Text style={styles.groupCardHeader}>Behavior Concerns</Text>
            <View style={styles.groupItemsContainer}>{behaviorRows}</View>
          </View>
        )}
      </View>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.centeredContainer}>
          <Text style={styles.centeredText}>Loading concerns…</Text>
        </View>
      );
    }
    if (selectedDomain?.key === 'measures') {
      return renderMeasuresDomain();
    }
    if (!concernData) {
      return (
        <View style={styles.centeredContainer}>
          <Text style={styles.centeredText}>{'No concern data available.\n\nUse DebugView → Concern Management → Extract Concerns to load AdminSegment 11272.'}</Text>
        </View>
      );
    }
    if (currentGroups.length === 0) {
      return (
        <View style={styles.centeredContainer}>
          <Text style={styles.centeredText}>{`${selectedDomain?.icon ?? ''}  No items found for ${selectedDomain?.name ?? ''} domain.\n\nThis domain may use a different MvarName pattern.\nAvailable groups: ${concernData.groups.map(g => `${g.mvarName}\u2192${g.domainKey}`).join(', ')}`}</Text>
        </View>
      );
    }
    return <>{currentGroups.map(renderGroup)}</>;
  };

  // â”€â”€â”€ JSX â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      {/* Fixed Domain Header */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
          <View style={styles.domainTitleRow}>
            <Text style={styles.domainTitle}>
              {selectedDomain?.icon}
              {'  '}
              {selectedDomain?.name}
              {' Concerns'}
            </Text>
            {showBackToSnapshot && onBackToSnapshot ? (
              <TouchableOpacity style={styles.backToSnapshotBtn} onPress={onBackToSnapshot}>
                <Text style={styles.backToSnapshotBtnText}>Back to Snapshot ▶</Text>
              </TouchableOpacity>
            ) : selectedDomainIndex === domains.length - 1 && onUpdateActions ? (
              <TouchableOpacity style={styles.updateActionsBtn} onPress={handleUpdateActions}>
                <Text style={styles.updateActionsBtnText}>Update Actions ▶</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.navigationRow}>
            <View style={styles.leftNavigationControls} />
            <View style={styles.centerDescription}>
              <Text style={styles.domainDescription}>
                {selectedDomain?.key === 'parent' && activeProfile?.name
                  ? `Concerns about ${activeProfile.name}'s behavior`
                  : selectedDomain?.key === 'family' && activeProfile?.name
                  ? `${activeProfile.name}'s concerns shared by the broader family unit`
                  : selectedDomain?.key === 'provider' && activeProfile?.name
                  ? `${activeProfile.name}'s clinical concerns`
                  : selectedDomain?.key === 'measures' && activeProfile?.name
                  ? `Concerns about ${activeProfile.name}'s measures result`
                  : selectedDomain?.description}
              </Text>
              {showLastUpdate && domainLastUpdated ? (
                <Text style={styles.domainStatusInfo}>
                  {'🕐 Updated: '}{formatComputationTimestamp(domainLastUpdated)}
                </Text>
              ) : domainStaleness.isStale ? (
                <Text style={styles.domainStatusWarning}>{'⚠️ Concerns need reviewing'}</Text>
              ) : domainLastUpdated === null ? (
                <Text style={styles.domainStatusInfo}>{'📋 Not yet reviewed'}</Text>
              ) : null}
            </View>
            <View style={styles.rightNavigationControls}><TouchableOpacity
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedDomainIndex === 0 ? styles.navButtonDisabled : null]}
                onPress={handlePrevDomain}
                disabled={selectedDomainIndex === 0}
              ><Text style={[styles.navButtonText, { fontSize: 20 }]}>{'◀'}</Text></TouchableOpacity><TouchableOpacity
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedDomainIndex === domains.length - 1 ? styles.navButtonDisabled : null]}
                onPress={handleNextDomain}
                disabled={selectedDomainIndex === domains.length - 1}
              ><Text style={[styles.navButtonText, { fontSize: 20 }]}>{'▶'}</Text></TouchableOpacity></View>
          </View>
        </View>
      </View>
      {/* Domain Tab Menu */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
          {domains.map((domain, index) => (
            <TouchableOpacity
              key={domain.key}
              style={[styles.tab, selectedDomainIndex === index ? { backgroundColor: appConfig.getNavButtonColor() } : null]}
              onPress={() => navigateToDomain(index)}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}><Text style={styles.tabIcon}>{domain.icon}</Text><Text style={[styles.tabText, selectedDomainIndex === index ? styles.selectedTabText : null]}>{domain.name}</Text></View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          <ScrollView style={styles.contentContainer} contentContainerStyle={styles.contentInner}>
            {renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

// â”€â”€â”€ Styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingTop: 12,
    paddingHorizontal: appConfig.getCardMargin(),
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: appConfig.getNavButtonColorInactive(),
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  backToSnapshotBtn: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 12,
    flexShrink: 0,
  },
  backToSnapshotBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  updateActionsBtn: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 12,
    flexShrink: 0,
  },
  updateActionsBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  domainStatusInfo: {
    fontSize: 11,
    color: '#cbd5e1',
    textAlign: 'center',
    marginTop: 4,
    opacity: 0.9,
  },
  domainStatusWarning: {
    fontSize: 11,
    color: '#fde68a',
    textAlign: 'center',
    marginTop: 4,
    fontWeight: '600',
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: appConfig.getNavButtonColor(),
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
  },
  contentInner: {
    padding: 16,
    paddingBottom: 32,
  },
  // ── Concern group card ──
  groupCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 16,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    borderWidth: 1,
    borderColor: '#e8edf2',
  },
  groupCardHeader: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a2b3c',
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e8edf2',
  },
  groupItemsContainer: {
    gap: 8,
  },
  // ── Individual concern item card ──
  itemCard: {
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 10,
  },
  itemCardChecked: {
    backgroundColor: '#eff8ff',
    borderColor: '#93c5fd',
  },
  itemCheckRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 4,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 8,
    backgroundColor: '#f0f4f8',
    borderWidth: 1,
    borderColor: '#d1dce8',
    alignItems: 'center',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#5f6368',
  },
  actionButtonTextSelected: {
    color: '#ffffff',
  },
  concernRow: {
    // kept for backward compatibility
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#bdc3c7',
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 1,
    flexShrink: 0,
  },
  checkboxChecked: {
    borderColor: '#3498db',
    backgroundColor: '#3498db',
  },
  checkmark: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: 18,
  },
  concernText: {
    flex: 1,
    fontSize: 15,
    color: '#2c3e50',
    lineHeight: 22,
  },
  concernTextChecked: {
    color: '#3498db',
    fontWeight: '500',
  },
  // â”€â”€ Empty / loading states â”€â”€
  centeredContainer: {
    paddingVertical: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centeredText: {
    fontSize: 15,
    color: '#7f8c8d',
    textAlign: 'center',
    lineHeight: 22,
  },
});
